import { useState, type ReactNode } from 'react';
import {
  Button,
  type ButtonSize,
  type ButtonVariant,
  Checkbox,
  Cluster,
  Columns,
  Container,
  FoldPanel,
  Input,
  Select,
  Snippet,
  SocialButton,
  SocialRow,
  Specimens,
  Stack,
  Well,
} from '@src/components';
import { ArrowRightIcon } from '@src/components/icons/ArrowRightIcon';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { MoreIcon } from '@src/components/icons/MoreIcon';
import { TrashIcon } from '@src/components/icons/TrashIcon';

const SIZES: ButtonSize[] = ['large', 'medium', 'small'];

const ICONS: Record<string, { node: ReactNode; component: string }> = {
  none: { node: null, component: '' },
  gift: { node: <GiftIcon />, component: 'GiftIcon' },
  arrow: { node: <ArrowRightIcon />, component: 'ArrowRightIcon' },
  trash: { node: <TrashIcon />, component: 'TrashIcon' },
  close: { node: <CloseIcon />, component: 'CloseIcon' },
  more: { node: <MoreIcon />, component: 'MoreIcon' },
};

const VARIANTS: ButtonVariant[] = ['primary', 'blue', 'secondary', 'soft', 'ghost', 'danger'];

interface PlaygroundState {
  variant: ButtonVariant;
  size: ButtonSize;
  icon: string;
  iconPosition: 'start' | 'end';
  label: string;
  disabled: boolean;
  fullWidth: boolean;
  iconOnly: boolean;
}

const INITIAL_STATE: PlaygroundState = {
  variant: 'primary',
  size: 'large',
  icon: 'gift',
  iconPosition: 'start',
  label: 'Create a game',
  disabled: false,
  fullWidth: false,
  iconOnly: false,
};

/** The JSX the current playground settings correspond to, for copy-pasting. */
function toSnippet(state: PlaygroundState): string {
  const props = [`variant="${state.variant}"`, `size="${state.size}"`];
  if (state.icon !== 'none') {
    props.push(`icon={<${ICONS[state.icon].component} />}`);
    if (state.iconPosition === 'end') {
      props.push('iconPosition="end"');
    }
  }
  if (state.disabled) {
    props.push('disabled');
  }
  if (state.fullWidth && !state.iconOnly) {
    props.push('fullWidth');
  }
  if (state.iconOnly) {
    props.push('aria-label="Close"');
    return `<Button ${props.join(' ')} />`;
  }
  return `<Button ${props.join(' ')}>${state.label}</Button>`;
}

function toOptions(values: string[]) {
  return values.map((value) => ({ value, label: value }));
}

export function ButtonSection() {
  const [state, setState] = useState<PlaygroundState>(INITIAL_STATE);
  const [openField, setOpenField] = useState<string | null>(null);

  function update<Key extends keyof PlaygroundState>(key: Key, value: PlaygroundState[Key]) {
    setState((current) => ({ ...current, [key]: value }));
  }

  function bindOpen(id: string) {
    return {
      open: openField === id,
      onOpenChange: (next: boolean) => setOpenField(next ? id : null),
    };
  }

  return (
    <FoldPanel
      id="buttons"
      title="Buttons"
      hint="Three sizes, five variants, an optional icon on either side — or the icon alone. One primary button per screen."
      example={`<Button variant="primary" size="large" icon={<GiftIcon />}>
  Create a game
</Button>`}
    >
      <Specimens label="Primary" note="The single most important action. Coral means click.">
        {SIZES.map((size) => (
          <Button key={size} size={size}>
            {size}
          </Button>
        ))}
        <Button icon={<GiftIcon />}>With icon</Button>
        <Button icon={<ArrowRightIcon />} iconPosition="end">
          Invite
        </Button>
        <Button disabled>Disabled</Button>
      </Specimens>

      <Specimens label="Secondary" note="The alternative path — outlined, never coral.">
        {SIZES.map((size) => (
          <Button key={size} variant="secondary" size={size}>
            {size}
          </Button>
        ))}
        <Button variant="secondary" icon={<ArrowRightIcon />} iconPosition="end">
          Quick draw
        </Button>
        <Button variant="secondary" disabled>
          Disabled
        </Button>
      </Specimens>

      <Specimens label="Soft" note="Lavender fill for supporting actions inside calm panels.">
        {SIZES.map((size) => (
          <Button key={size} variant="soft" size={size}>
            {size}
          </Button>
        ))}
        <Button variant="soft" icon={<GiftIcon />}>
          With icon
        </Button>
        <Button variant="soft" disabled>
          Disabled
        </Button>
      </Specimens>

      <Specimens label="Ghost" note="Dismiss, cancel, back — anything that undoes.">
        {SIZES.map((size) => (
          <Button key={size} variant="ghost" size={size}>
            {size}
          </Button>
        ))}
        <Button variant="ghost" disabled>
          Disabled
        </Button>
      </Specimens>

      <Specimens label="Danger" note="Destructive only, and never the primary button.">
        {SIZES.map((size) => (
          <Button key={size} variant="danger" size={size} icon={<TrashIcon />}>
            {size}
          </Button>
        ))}
        <Button variant="danger" disabled icon={<TrashIcon />}>
          Disabled
        </Button>
      </Specimens>

      <Specimens
        label="Icon only"
        note="Drop the label, keep an aria-label. Same variants; the square is 44 / 32 / 28."
      >
        {VARIANTS.map((variant) => (
          <Button
            key={variant}
            variant={variant}
            icon={variant === 'ghost' ? <MoreIcon /> : <CloseIcon />}
            aria-label={variant}
          />
        ))}
        {SIZES.map((size) => (
          <Button key={size} size={size} icon={<CloseIcon />} aria-label={size} />
        ))}
        <Button variant="ghost" icon={<MoreIcon />} disabled aria-label="More, disabled" />
      </Specimens>

      <Specimens label="Full width" note="Stretches to its container — used on narrow screens.">
        <Container width="narrow">
          <Button fullWidth icon={<GiftIcon />}>
            Create a game
          </Button>
        </Container>
      </Specimens>

      <Specimens
        label="Social"
        note="Google, Telegram, VK. Marks come from the v1 icons. .com shows Google + Telegram; .ru shows VK."
      >
        <Container width="narrow">
          <Stack gap={3}>
            <SocialRow columns={2}>
              <SocialButton kind="google">Google</SocialButton>
              <SocialButton kind="telegram">Telegram</SocialButton>
            </SocialRow>
            <SocialButton kind="vk">Войти через VK ID</SocialButton>
          </Stack>
        </Container>
      </Specimens>

      <Specimens label="Playground" note="Change the props and see the result.">
        <Well>
          <Stack gap={4} wide>
            <Columns columns="fill" gap={4}>
              <Select
                label="Variant"
                value={state.variant}
                options={toOptions(VARIANTS)}
                onValueChange={(value) => update('variant', value as ButtonVariant)}
                {...bindOpen('variant')}
              />
              <Select
                label="Size"
                value={state.size}
                options={toOptions(SIZES)}
                onValueChange={(value) => update('size', value as ButtonSize)}
                {...bindOpen('size')}
              />
              <Select
                label="Icon"
                value={state.icon}
                options={toOptions(Object.keys(ICONS))}
                onValueChange={(value) => update('icon', value)}
                {...bindOpen('icon')}
              />
              <Select
                label="Icon side"
                value={state.iconPosition}
                options={toOptions(['start', 'end'])}
                onValueChange={(value) => update('iconPosition', value as 'start' | 'end')}
                disabled={state.icon === 'none'}
                {...bindOpen('iconPosition')}
              />
              <Input
                label="Label"
                value={state.label}
                onValueChange={(value) => update('label', value)}
              />
            </Columns>
            <Cluster gap={4}>
              <Checkbox
                label="disabled"
                checked={state.disabled}
                onCheckedChange={(checked) => update('disabled', checked)}
              />
              <Checkbox
                label="fullWidth"
                checked={state.fullWidth}
                onCheckedChange={(checked) => update('fullWidth', checked)}
              />
              <Checkbox
                label="icon only"
                checked={state.iconOnly}
                onCheckedChange={(iconOnly) => {
                  setState((current) => ({
                    ...current,
                    iconOnly,
                    icon: iconOnly && current.icon === 'none' ? 'close' : current.icon,
                  }));
                }}
              />
            </Cluster>
            <Well tone="stage">
              <Button
                variant={state.variant}
                size={state.size}
                icon={ICONS[state.icon].node}
                iconPosition={state.iconPosition}
                disabled={state.disabled}
                fullWidth={state.fullWidth && !state.iconOnly}
                aria-label={state.iconOnly ? state.label || 'Button' : undefined}
              >
                {state.iconOnly ? undefined : state.label}
              </Button>
            </Well>
            <Snippet>{toSnippet(state)}</Snippet>
          </Stack>
        </Well>
      </Specimens>
    </FoldPanel>
  );
}
